import React, { useState } from 'react'
import API from 'api'
import { getValue } from 'firebase-utils'
import { makeStyles } from '@material-ui/core'
import { Button } from 'foundations-library/components'
import { Dialog, Dropzone } from 'functional-foundations'

const handleFileDownload = (apiResponse) => {
    // 1. Extract the data from your specific object structure
    const { body, headers } = apiResponse.custom_http_response

    // 2. Parse the filename from Content-Disposition
    // (Falling back to 'download.pdf' if parsing fails)
    const filenameMatch = headers['Content-Disposition'].match(/filename="(.+)"/)
    const fileName = filenameMatch ? filenameMatch[1] : 'download.pdf'
    const contentType = headers['Content-Type']

    // 3. Convert Base64 string to a Byte Array
    const byteCharacters = atob(body)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)

    // 4. Create a Blob and an Object URL
    const blob = new Blob([byteArray], { type: contentType })
    const url = window.URL.createObjectURL(blob)

    // 5. Create a temporary anchor element and trigger click
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()

    // 6. Cleanup: Remove the link and revoke the URL to save memory
    link.remove()
    window.URL.revokeObjectURL(url)
}
/**
 *
 */
export type EncodedFile = {
    filename: string,
    file: string
    size: number
    category: 'privacy_policy' | 'terms_and_conditions' | 'call_to_action',
    csp_document_id?: number,
    csp_campaign_id?: number
}

type ErrorTypeType = 'remove-file' | 'download-file' | 'upload-files'
type ErrorValueType = {
    title: string,
    content: string
}

type ErrorType = Record<ErrorTypeType, ErrorValueType>

const jss = () => ({
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-around',
        marginTop: 30
    }
})

const useStyles = makeStyles(jss)

const convertFileToBase64 = (uploadedFile: File, item: Recort<string, unknown>): Promise<EncodedFile> => new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
        const result = typeof reader.result === 'string' ? reader.result : ''
        const base64Content = result.includes(',') ? result.split(',')[1] : result

        resolve({
            filename: uploadedFile.name,
            file: base64Content,
            size: uploadedFile.size,
            category: 'call_to_action',
            csp_campaign_id: item?.csp_campaign_id
        })
    }

    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(uploadedFile)
})

const isFile = (obj) => obj &&
    typeof obj === 'object' &&
    obj.constructor &&
    obj.constructor.name === 'File'
interface PropsInterface {
    value: unknown[],
    onChange(v: unknown[]): unknown
    disabled: boolean,
    item?: Record<string, unknown>
}

/**
 *
 */
const FileUploader = ({
    value,
    onChange,
    disabled = false,
    item
}: PropsInterface): JSX.Element => {
    const styles = useStyles()
    const [error, setError] = useState<ErrorType | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [loadingFilesIndexes, setLoadingFilesIndexes] = useState([])

    const handleFilesChange = async (files: File[]) => {
        const unconverted: File[] = []
        const converted: EncodedFile[] = []
        files.map((file) => {
            if (isFile(file)) {
                unconverted.push(file)
            } else {
                converted.push(file)
            }
            return true
        })

        try {
            const newFiles = await Promise.all(unconverted.map((x) => convertFileToBase64(x, item)))
            setLoading(true)
            const newFilesUploaded = await API.upload10DLCFiles(await Promise.all(newFiles))
            const nextEncodedFiles: EncodedFile[] = []
            newFilesUploaded.map((x, i) => {
                if (x.message === 'success') {
                    nextEncodedFiles.push({
                        ...newFiles[i],
                        csp_document_id: x.document_id
                    })
                } else {
                    const deletePromises = nextEncodedFiles.map((x) => API.remove10DLCFile(x.csp_document_id))
                    Promise.all(deletePromises) // without await on purpose, cleanup
                    throw new Error('Upload failed')
                }

                return true
            })

            onChange([...converted, ...nextEncodedFiles])
        } catch (err) {
            setError({
                title: 'Upload error',
                content: 'Your file is too large or not in a supported format. Please upload a PDF, JPG, or PNG file under 10MB and try again.'
            })
        } finally {
            setLoading(false)
        }
    }
    const onFileRemoved = async (f) => {
        const id = f.csp_document_id || f.document_id
        if (!id) return null

        try {
            onChange(value.filter((x) => x.csp_document_id !== id)) // for visibility
            const res = await API.remove10DLCFile(id)
            if (res.message !== 'success') throw new Error('Remove file error')
        } catch (error) {
            setError({
                title: 'Remove error',
                content: 'Something went wrong while removing your file. Please try again.'
            })
            onChange([
                ...value.filter((x) => x.csp_document_id !== id),
                f
            ])
        }
    }

    const onClickFile = async (f, index) => {
        const id = f.csp_document_id || f.document_id
        if (!id) return null
        setLoadingFilesIndexes(prev => [...prev, index])
        try {
            const res = await API.download10DLCFile(id)
            await handleFileDownload(res)
        } catch (error) {
            console.log(error)
            setError({
                title: 'Download error',
                content: 'Something went wrong while downloading your file. Please try again.'
            })
        } finally {
            setLoadingFilesIndexes(prev => prev.filter((x) => x !== index))
        }
    }

    return <div>
        <Dropzone
            label={getValue('sms_enrollment_campaigns_1st_step_paper_file_upload_label')}
            value={value}
            maxFilesCount={5}
            onChange={handleFilesChange}
            required
            disabled={disabled}
            onClickRemove={onFileRemoved}
            onClickFile={onClickFile}
            loading={loading}
            loadingFilesIndexes={loadingFilesIndexes}
            sampleDocumentUrl={getValue('sms_enrollment_campaigns_1st_step_sample_document_url')}
            data-testid='file-uploader'
        />
        {
            error
                ? <Dialog isOpen title={error?.title} width={600} data-testid='file-uploader-dialog'>
                    {error?.content}
                    <div className={styles.buttonContainer}>
                        <Button
                            variant='outlined'
                            color='neutral'
                            onClick={() => setError(null)}
                            data-testid='file-uploader-dialog-close-btn'
                        >Close</Button>
                    </div>
                </Dialog>
                : <></>
        }
    </div>
}

export default FileUploader
